// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 26-versioning-update-strategy.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime, timeUntil } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import type { UpdaterPosture } from '@greenlight/shared';

export interface UpdaterHeadline {
  variant: StatusBadgeVariant;
  label: string;
  /** The release the install could move to, when that is the point of the line. */
  version: string | null;
  /** Why the headline says what it says, in the operator's words. */
  note: string | null;
}

/**
 * The single sentence the Updater card leads with.
 *
 * Enablement outranks currency: on an install that does not self-update, "an
 * update is available" is true but answers a question nobody asked first, and
 * the next-firing time it sits beside would be meaningless. The available
 * release is still named, so leading with enablement never hides it.
 *
 * Currency has three states, not two. A `null` verdict means the comparison
 * could not be made — the running release or the newest seen one was
 * unreadable — which is not the same as being current, and must never render
 * as "Up to date".
 */
export function updaterHeadline(posture: UpdaterPosture): UpdaterHeadline {
  const behind = posture.up_to_date === false ? posture.newest_release_seen : null;

  if (posture.updates_enabled === false) {
    return {
      variant: 'inactive',
      label: 'Automatic updates are off',
      version: behind,
      note: 'This install has no updater CronJob, so it will not update itself.',
    };
  }
  if (posture.updates_enabled === null) {
    return {
      variant: 'inactive',
      label: 'Can’t tell whether updates are on',
      version: behind,
      note: posture.cluster_credentials_rejected
        ? 'The cluster rejected the control plane’s credentials. Kubernetes reissues them on its own within the hour; nothing needs restarting.'
        : 'The updater namespace could not be read — most often a missing RBAC binding.',
    };
  }
  if (posture.up_to_date === true) {
    return { variant: 'active', label: 'Up to date', version: null, note: null };
  }
  if (posture.up_to_date === false) {
    return { variant: 'degraded', label: 'An update is available', version: behind, note: null };
  }
  if (posture.newest_release_seen !== null) {
    return {
      variant: 'inactive',
      label: 'Newest release seen',
      version: posture.newest_release_seen,
      note: 'The running release could not be read, so this install’s currency is unknown.',
    };
  }
  return {
    variant: 'inactive',
    label: 'No update check has run yet',
    version: null,
    note: null,
  };
}

/**
 * The forward-looking answer, ahead of any history: whether this install
 * updates itself, whether it is behind, and when it will next try.
 *
 * One line rather than a grid of cells, because the control plane reads most of
 * those values from off-cluster and has nothing to report for them on an
 * install it cannot see into. The rest live in {@link UpdaterPostureDetail},
 * where each missing one says why.
 */
export function UpdaterPostureSummary({
  posture,
  isLoading,
  isError,
}: {
  readonly posture: UpdaterPosture | null;
  readonly isLoading: boolean;
  readonly isError: boolean;
}): React.ReactElement {
  if (isLoading) {
    return (
      <div data-testid="updater-posture">
        <Skeleton className="h-5 w-56" />
        <Skeleton className="mt-2 h-3 w-40" />
      </div>
    );
  }

  if (isError || posture === null) {
    return (
      <p className="text-sm text-muted-foreground" data-testid="updater-posture">
        Couldn’t read the update posture. Retry in a moment.
      </p>
    );
  }

  const headline = updaterHeadline(posture);

  return (
    <div className="flex flex-col gap-1" data-testid="updater-posture">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <StatusBadge variant={headline.variant}>{headline.label}</StatusBadge>
        {headline.version !== null && <MonoValue>{headline.version}</MonoValue>}
        {posture.next_run_at !== null && (
          <span className="text-sm text-muted-foreground">
            · next run <span title={posture.next_run_at}>{timeUntil(posture.next_run_at)}</span>
          </span>
        )}
      </div>
      {headline.note !== null ? (
        <p className="text-[13px] leading-relaxed text-muted-foreground">{headline.note}</p>
      ) : posture.last_check_at !== null ? (
        <p className="text-[13px] text-muted-foreground">
          Checked {relativeTime(posture.last_check_at)}.
        </p>
      ) : null}
    </div>
  );
}

function Field({
  label,
  children,
  note,
}: {
  label: string;
  children: React.ReactNode;
  note?: string;
}): React.ReactElement {
  // dt/dd stay direct children of one wrapping div: that is the only grouping a
  // <dl> allows, and nesting deeper drops the term/value pairing for a screen
  // reader. The note rides inside the dd for the same reason.
  return (
    <div className="flex items-baseline justify-between gap-3 border-t border-border py-2 text-sm first:border-t-0 first:pt-0 last:pb-0">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="min-w-0 text-right font-medium text-foreground">
        {children}
        {note !== undefined && (
          <p className="mt-0.5 text-[11px] leading-snug font-normal text-muted-foreground">
            {note}
          </p>
        )}
      </dd>
    </div>
  );
}

/**
 * A value the card cannot fill in. An operator needs to know which kind of
 * blank they are looking at, so the two never share wording: "Not reported" is
 * what the control plane could not see, and everything else states a fact about
 * the install. Schedule, next run, and last firing are all read off the updater
 * CronJob, so with updates off all three are absent by the one fact — there is
 * no CronJob to describe — and say so in the one phrase, while an unreadable
 * namespace leaves them genuinely unreported.
 */
const Absent = ({ children }: { children: React.ReactNode }): React.ReactElement => (
  <span className="font-normal text-muted-foreground">{children}</span>
);

const NotReported = (): React.ReactElement => <Absent>Not reported</Absent>;

const NAMESPACE_UNREADABLE = 'The updater namespace could not be read.';

/** Whether a CronJob-derived row is blank because the namespace was unreadable. */
const namespaceNote = (posture: UpdaterPosture): string | undefined =>
  posture.updates_enabled === null ? NAMESPACE_UNREADABLE : undefined;

/** Every posture value the headline leaves out, each missing one saying why. */
export function UpdaterPostureDetail({
  posture,
}: {
  readonly posture: UpdaterPosture;
}): React.ReactElement {
  return (
    <dl data-testid="updater-posture-detail">
      <Field
        label="Automatic updates"
        note={
          posture.updates_enabled === false
            ? 'No updater CronJob on this install.'
            : namespaceNote(posture)
        }
      >
        {posture.updates_enabled === null ? 'Can’t tell' : posture.updates_enabled ? 'On' : 'Off'}
      </Field>

      <Field label="Schedule" note={posture.schedule === null ? namespaceNote(posture) : undefined}>
        {posture.schedule !== null ? (
          <span>
            <MonoValue>{posture.schedule}</MonoValue>
            {posture.time_zone !== null && (
              <span className="font-normal text-muted-foreground"> · {posture.time_zone}</span>
            )}
          </span>
        ) : posture.updates_enabled === false ? (
          <Absent>Not scheduled</Absent>
        ) : (
          <NotReported />
        )}
      </Field>

      <Field
        label="Next run"
        note={
          posture.next_run_at !== null
            ? undefined
            : posture.updates_enabled === true
              ? 'The schedule did not resolve to a firing time.'
              : namespaceNote(posture)
        }
      >
        {posture.next_run_at !== null ? (
          <span title={posture.next_run_at}>{timeUntil(posture.next_run_at)}</span>
        ) : posture.updates_enabled === false ? (
          <Absent>Not scheduled</Absent>
        ) : (
          <NotReported />
        )}
      </Field>

      <Field
        label="Last firing"
        note={
          posture.last_scheduled_at !== null
            ? undefined
            : posture.updates_enabled === true
              ? 'The CronJob has not created a Job yet.'
              : namespaceNote(posture)
        }
      >
        {posture.last_scheduled_at !== null ? (
          <span title={posture.last_scheduled_at}>{relativeTime(posture.last_scheduled_at)}</span>
        ) : posture.updates_enabled === true ? (
          <Absent>No firings recorded</Absent>
        ) : posture.updates_enabled === false ? (
          <Absent>Not scheduled</Absent>
        ) : (
          <NotReported />
        )}
      </Field>

      <Field
        label="Takes prereleases"
        note={
          posture.include_prereleases === null
            ? 'The chart sets this only where the updater is enabled.'
            : undefined
        }
      >
        {posture.include_prereleases === null ? (
          <NotReported />
        ) : posture.include_prereleases ? (
          'Yes'
        ) : (
          'No'
        )}
      </Field>

      <Field
        label="Newest release seen"
        note={posture.last_check_at === null ? 'No check has run yet.' : undefined}
      >
        {posture.newest_release_seen !== null ? (
          <span>
            <MonoValue>{posture.newest_release_seen}</MonoValue>
            {posture.last_check_at !== null && (
              <span className="font-normal text-muted-foreground">
                {' '}
                · checked {relativeTime(posture.last_check_at)}
              </span>
            )}
          </span>
        ) : (
          <NotReported />
        )}
      </Field>
    </dl>
  );
}
